"use client";

import React, { useState, useEffect } from "react";
import axios from "axios";
import { Notify } from "notiflix/build/notiflix-notify-aio";
import { useTranslation } from "react-i18next";
import "bootstrap/dist/css/bootstrap.min.css";

import "./Getorder.css";
import "./modal.css";

export default function WarehouseOrders() {
    const { t } = useTranslation();

    const TABS = [
        { label: t('warehouseOrders.tabs.new'),      value: "SHIPPED"          },
        { label: t('warehouseOrders.tabs.accepted'), value: "IN_KZ_WAREHOUSE"  },
        { label: t('warehouseOrders.tabs.ready'),    value: "READY_FOR_PICKUP" },
        { label: t('warehouseOrders.tabs.finished'), value: "DELIVERED"        },
    ];

    const [orders,        setOrders]        = useState([]);
    const [searchTerm,    setSearchTerm]    = useState("");
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showModal,     setShowModal]     = useState(false);

    const [qrItems, setQrItems] = useState([]);

    const [currentTab, setCurrentTab] = useState("SHIPPED");
    const [page,       setPage]       = useState(0);
    const size = 10;
    const [totalPages, setTotalPages] = useState(0);

    const apiBase = process.env.REACT_APP_API_BASE_URL;

    const fetchOrders = async (trackStatus, pg) => {
        try {
            const token = sessionStorage.getItem("accessToken");
            if (!token) throw new Error(t('warehouseOrders.errors.noAccess'));
            const { data } = await axios.get(
                `${apiBase}/worker/orders/by-track-status`,
                { params: { trackStatus, page: pg, size }, headers: { Authorization: `Bearer ${token}` } }
            );
            setOrders(data.content || []);
            setTotalPages(data.totalPages || 0);
        } catch (err) {
            console.error(err);
            Notify.failure(t('warehouseOrders.errors.loadFailed'));
        }
    };

    const fetchQRCodes = async (orderId) => {
        try {
            const token = sessionStorage.getItem("accessToken");
            const { data } = await axios.get(
                `${apiBase}/worker/${orderId}/qrcodes`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            return data.orderItems ?? [];
        } catch (err) {
            console.error(err);
            Notify.failure(t('warehouseOrders.errors.qrLoadFailed'));
            return [];
        }
    };

    useEffect(() => { fetchOrders(currentTab, page); }, [currentTab, page]);

    const filtered = orders.filter((o) => {
        let txt = "";
        Object.entries(o).forEach(([k, v]) => { if (k !== "items") txt += v + " "; });
        o.items?.forEach((i) => { txt += i.name + " " + i.quantity + " "; });
        return txt.toLowerCase().includes(searchTerm.toLowerCase());
    });

    const openModal = async (o) => {
        setSelectedOrder(o);
        setShowModal(true);
        setQrItems(await fetchQRCodes(o.orderId));
    };
    const closeModal = () => setShowModal(false);

    const renderModal = () => {
        if (!showModal || !selectedOrder) return null;
        const o = selectedOrder;

        return (
            <div className="modal-overlay" onClick={closeModal}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h5 className="modal-title">{t('warehouseOrders.modal.title')} #{o.orderId}</h5>
                        <button className="close-button" onClick={closeModal}>&times;</button>
                    </div>

                    <div className="modal-body overflow-auto">
                        <p><strong>{t('warehouseOrders.modal.orderDate')}:</strong> {new Date(o.orderDate).toLocaleString()}</p>
                        <p><strong>{t('warehouseOrders.modal.trackStatus')}:</strong> {o.trackStatus}</p>
                        <p><strong>{t('warehouseOrders.modal.amount')}:</strong> {o.totalPrice}</p>
                        <p><strong>{t('warehouseOrders.modal.name')}:</strong> {o.firstname} {o.lastname}</p>

                        <h6 className="mt-3">{t('warehouseOrders.modal.items')}:</h6>
                        {o.items?.length
                            ? o.items.map((i, idx) => <div key={idx}>{i.name} × {i.quantity}</div>)
                            : <p>{t('warehouseOrders.modal.noItems')}</p>}

                        {qrItems.length > 0 && (
                            <>
                                <h6 className="mt-4">{t('warehouseOrders.modal.qrCodes')}:</h6>
                                <div className="d-flex flex-wrap gap-3">
                                    {qrItems.map((it) => (
                                        <div key={it.productId} className="text-center">
                                            <img
                                                src={`data:image/png;base64,${it.qrCodeBase64}`}
                                                alt={`QR product ${it.productId}`}
                                                className="img-thumbnail shadow"
                                                style={{ width: 120, height: 120 }}
                                            />
                                            <small className="d-block mt-1">ID {it.productId}</small>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const getNextStatus = (s) => ({
        SHIPPED:          "IN_KZ_WAREHOUSE",
        IN_KZ_WAREHOUSE:  "READY_FOR_PICKUP",
        READY_FOR_PICKUP: "DELIVERED",
    })[s];

    const updateStatus = async (orderId, newStatus) => {
        try {
            const token = sessionStorage.getItem("accessToken");
            await axios.get(
                `${apiBase}/worker/update/track-status/${orderId}`,
                { params: { trackStatus: newStatus }, headers: { Authorization: `Bearer ${token}` } }
            );
            Notify.success(`${t('warehouseOrders.success.statusUpdated')} → ${newStatus}`);
            setCurrentTab(newStatus);
            setPage(0);
        } catch (e) {
            console.error(e);
            Notify.failure(t('warehouseOrders.errors.statusUpdateFailed'));
        }
    };

    return (
        <div className="mt-4">
            <div className="mb-3">
                {TABS.map((t) => (
                    <button
                        key={t.value}
                        className={`btn me-2 ${currentTab === t.value ? "bg-danger active text-white" : "btn-outline-primary"}`}
                        onClick={() => setCurrentTab(t.value)}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            <div className="row mb-3">
                <div className="col">
                    <input
                        type="text"
                        className="form-control"
                        placeholder={t('warehouseOrders.searchPlaceholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {filtered.length ? (
                <div className="table-responsive">
                    <table className="table table-hover">
                        <thead className="thead-light">
                        <tr>
                            <th>{t('warehouseOrders.table.id')}</th>
                            <th>{t('warehouseOrders.table.orderDate')}</th>
                            <th>{t('warehouseOrders.table.trackStatus')}</th>
                            <th>{t('warehouseOrders.table.amount')}</th>
                            <th>{t('warehouseOrders.table.name')}</th>
                            <th>{t('warehouseOrders.table.items')}</th>
                            <th>{t('warehouseOrders.table.actions')}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {filtered.map((o) => (
                            <tr key={o.orderId}>
                                <td>{o.orderId}</td>
                                <td>{new Date(o.orderDate).toLocaleString()}</td>
                                <td>{o.trackStatus}</td>
                                <td>{o.totalPrice}</td>
                                <td>{o.firstname}</td>
                                <td>{o.items?.map((i, idx) => <div key={idx}>{i.name} × {i.quantity}</div>)}</td>
                                <td>
                                    <button className="btn btn-info btn-sm me-2" onClick={() => openModal(o)}>
                                        {t('actions.details')}
                                    </button>
                                    {getNextStatus(o.trackStatus) && (
                                        <button
                                            className="btn btn-success btn-sm"
                                            onClick={() => updateStatus(o.orderId, getNextStatus(o.trackStatus))}
                                        >
                                            {o.trackStatus === "IN_KZ_WAREHOUSE" ? t('warehouseOrders.buttons.ship') : t('warehouseOrders.buttons.accept')}
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            ) : <p className="text-center">{t('warehouseOrders.noOrders')}</p>}

            <div className="d-flex justify-content-center align-items-center my-3">
                <button className="btn btn-outline-primary me-2" disabled={page <= 0}
                        onClick={() => setPage((p) => Math.max(p - 1, 0))}>← {t('warehouseOrders.pagination.prev')}
                </button>
                <span>{t('warehouseOrders.pagination.page')} {page + 1} {t('warehouseOrders.pagination.of')} {totalPages}</span>
                <button className="btn btn-outline-primary ms-2" disabled={page >= totalPages - 1}
                        onClick={() => setPage((p) => Math.min(p + 1, totalPages - 1))}>{t('warehouseOrders.pagination.next')} →
                </button>
            </div>

            {renderModal()}
        </div>
    );
}